var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MetadataTypes } from '@sisense/sdk-data';
import { PivotClient } from '@sisense/sdk-pivot-client';
import { ExecutionResultStatus } from '@sisense/task-manager';
import { QueryApiDispatcher } from './query-api-dispatcher/query-api-dispatcher.js';
import { QueryTaskManager } from './query-task-manager/query-task-manager.js';
import { PivotQueryTaskPassport, QueryTaskPassport, } from './query-task-manager/query-task-passport.js';
import { TranslatableError } from './translation/translatable-error.js';
/** @internal */
export const QUERY_DEFAULT_LIMIT = 20000;
const UNSPECIFIED_REASON = 'Unspecified reason';
export class DimensionalQueryClient {
    constructor(httpClient, pivotClient = new PivotClient(httpClient.url, httpClient.auth), shouldSkipHighlightsWithoutAttributes) {
        validateHttpClient(httpClient);
        this.queryApi = new QueryApiDispatcher(httpClient);
        this.taskManager = new QueryTaskManager(this.queryApi, pivotClient);
        this.shouldSkipHighlightsWithoutAttributes = shouldSkipHighlightsWithoutAttributes !== null && shouldSkipHighlightsWithoutAttributes !== void 0 ? shouldSkipHighlightsWithoutAttributes : false;
    }
    /**
     * Executes query
     *
     * @param queryDescription - all options that describe query
     * @param config - query execution configuration
     * @returns promise that resolves to query result data and cancel function that can be used to cancel sent query
     * @throws Error if query description is invalid
     */
    executeQuery(queryDescription, config) {
        const taskPassport = new QueryTaskPassport('SEND_JAQL_QUERY', queryDescription, Object.assign(Object.assign({}, (config ? config : {})), { shouldSkipHighlightsWithoutAttributes: this.shouldSkipHighlightsWithoutAttributes || false }));
        return {
            resultPromise: new Promise((resolve, reject) => {
                validateQueryDescription(queryDescription);
                void this.taskManager.executeQuerySending(taskPassport).then((executionResult) => {
                    if (executionResult.status === ExecutionResultStatus.SUCCESS) {
                        resolve(executionResult.result);
                    }
                    else {
                        reject(executionResult.error);
                    }
                });
            }),
            cancel: (reason) => this.taskManager.cancel(taskPassport.taskId, reason || UNSPECIFIED_REASON),
        };
    }
    executeCsvQuery(queryDescription, config) {
        const taskPassport = new QueryTaskPassport('SEND_DOWNLOAD_CSV_QUERY', queryDescription, Object.assign(Object.assign({}, (config ? config : {})), { shouldSkipHighlightsWithoutAttributes: this.shouldSkipHighlightsWithoutAttributes || false }));
        return {
            resultPromise: new Promise((resolve, reject) => {
                validateQueryDescription(queryDescription);
                void this.taskManager.executeDownloadCsvSending(taskPassport).then((executionResult) => {
                    if (executionResult.status === ExecutionResultStatus.SUCCESS) {
                        resolve(executionResult.result);
                    }
                    else {
                        reject(executionResult.error);
                    }
                });
            }),
            cancel: (reason) => this.taskManager.cancel(taskPassport.taskId, reason || UNSPECIFIED_REASON),
        };
    }
    /**
     * Executes pivot query
     *
     * @param pivotQueryDescription - all options that describe the pivot query
     * @param config - query execution configuration
     * @returns promise that resolves to pivot query result data and cancel function that can be used to cancel sent query
     * @throws Error if query description is invalid
     */
    executePivotQuery(pivotQueryDescription, config) {
        const taskPassport = new PivotQueryTaskPassport('SEND_JAQL_QUERY', pivotQueryDescription, Object.assign(Object.assign({}, (config ? config : {})), { shouldSkipHighlightsWithoutAttributes: this.shouldSkipHighlightsWithoutAttributes || false }));
        return {
            resultPromise: new Promise((resolve, reject) => {
                validatePivotQueryDescription(pivotQueryDescription);
                void this.taskManager.executePivotQuerySending(taskPassport).then((executionResult) => {
                    if (executionResult.status === ExecutionResultStatus.SUCCESS) {
                        resolve(executionResult.result);
                    }
                    else {
                        reject(executionResult.error);
                    }
                });
            }),
            cancel: (reason) => this.taskManager.cancel(taskPassport.taskId, reason || UNSPECIFIED_REASON),
        };
    }
    getDataSourceFields(dataSource, count = 9999, offset = 0) {
        return __awaiter(this, void 0, void 0, function* () {
            const dataSourceFields = yield this.queryApi.getDataSourceFields(dataSource, count, offset);
            return dataSourceFields || [];
        });
    }
    /**
     * Returns the schema of a data source by its name.
     */
    getDataSourceSchema(datasourceName) {
        return this.queryApi.getDataSourceSchema(datasourceName);
    }
    /**
     * Returns a list of data sources
     */
    getDataSourceList() {
        return __awaiter(this, void 0, void 0, function* () {
            const dataSourceList = yield this.queryApi.getDataSourceList();
            return dataSourceList || [];
        });
    }
}
/**
 * Validates query description
 *
 * @param queryDescription - query description to validate
 * @param config - query execution configuration
 * @throws Error if query description is invalid
 */
export function validateQueryDescription(queryDescription) {
    const { attributes, measures, filters, highlights, count, offset } = queryDescription;
    if (count && count < 0) {
        throw new TranslatableError('errors.invalidCountNegative', { count: count.toString() });
    }
    if (offset && offset < 0) {
        throw new TranslatableError('errors.invalidOffset', { offset: offset.toString() });
    }
    if (attributes.length + measures.length === 0) {
        throw new TranslatableError('errors.noDimensionsOrMeasures');
    }
    attributes.forEach((attribute) => {
        if (!attribute.skipValidation && !MetadataTypes.isAttribute(attribute)) {
            const { name: attributeName } = attribute;
            throw new TranslatableError('errors.invalidAttribute', { attributeName });
        }
    });
    measures.forEach((measure) => {
        if (!measure.skipValidation && !MetadataTypes.isMeasure(measure)) {
            throw new TranslatableError('errors.invalidMeasure', { measureName: measure.name });
        }
    });
    filters.forEach((filter) => {
        if (!filter.skipValidation && !MetadataTypes.isFilter(filter)) {
            throw new TranslatableError('errors.invalidFilter', { filterName: filter.name });
        }
    });
    highlights.forEach((highlight) => {
        if (!highlight.skipValidation && !MetadataTypes.isFilter(highlight)) {
            throw new TranslatableError('errors.invalidHighlight', { highlightName: highlight.name });
        }
    });
}
/**
 * Validates pivot query description
 *
 * @param queryDescription - pivot query description to validate
 * @throws Error if query description is invalid
 */
export function validatePivotQueryDescription(queryDescription) {
    const { rowsAttributes, columnsAttributes, measures, filters, highlights, count, offset } = queryDescription;
    if (count && count < 0) {
        throw new TranslatableError('errors.invalidCountNegative', { count: count.toString() });
    }
    if (offset && offset < 0) {
        throw new TranslatableError('errors.invalidOffset', { offset: offset.toString() });
    }
    if (rowsAttributes.length + columnsAttributes.length + measures.length === 0) {
        throw new TranslatableError('errors.noDimensionsOrMeasures');
    }
    filters.forEach((filter) => {
        if (!filter.skipValidation && !MetadataTypes.isFilter(filter)) {
            throw new TranslatableError('errors.invalidFilter', { filterName: filter.name });
        }
    });
    highlights.forEach((highlight) => {
        if (!highlight.skipValidation && !MetadataTypes.isFilter(highlight)) {
            throw new TranslatableError('errors.invalidHighlight', { highlightName: highlight.name });
        }
    });
}
/**
 * Validates http client
 *
 * @param httpClient - http client to validate
 * @throws Error if http client is invalid
 */
function validateHttpClient(httpClient) {
    if (!httpClient) {
        throw new TranslatableError('errors.missingHttpClient');
    }
    if (!httpClient.post) {
        throw new TranslatableError('errors.missingPostMethod');
    }
}
//# sourceMappingURL=query-client.js.map